import React, { useState } from "react";
import { isDebugUIEnabled } from "../../utils/uiMessageStream";

/**
 * DebugDetailsPanel
 * 
 * Collapsible panel that shows tool usage and debug info for a message.
 * Only visible when DEBUG_UI is enabled (via URL param or localStorage).
 * 
 * @param {Object} props
 * @param {string[]} props.toolsUsed - Array of tool names that were used
 * @param {Object[]} props.debugParts - Array of debug data parts
 * @param {Object} props.usageInfo - Token usage information
 */
const DebugDetailsPanel = ({ toolsUsed = [], debugParts = [], usageInfo = null }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Only show if DEBUG_UI is enabled
  if (!isDebugUIEnabled()) {
    return null;
  }

  // Don't render if there's nothing to show
  if (toolsUsed.length === 0 && debugParts.length === 0 && !usageInfo) {
    return null;
  }

  return (
    <div 
      className="debug-details-panel"
      style={{
        marginTop: "8px",
        fontSize: "12px",
        borderTop: "1px dashed rgba(128, 128, 128, 0.3)",
        paddingTop: "8px",
      }}
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          background: "none",
          border: "1px solid rgba(128, 128, 128, 0.3)",
          borderRadius: "4px",
          padding: "4px 8px",
          cursor: "pointer",
          fontSize: "11px",
          color: "#888",
          display: "flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <span style={{ fontSize: "10px" }}>{isExpanded ? "▼" : "▶"}</span>
        <span>Debug Details</span>
        {toolsUsed.length > 0 && (
          <span style={{ 
            background: "rgba(100, 100, 255, 0.2)", 
            borderRadius: "10px", 
            padding: "0 6px",
            marginLeft: "4px"
          }}>
            {toolsUsed.length} tool{toolsUsed.length > 1 ? "s" : ""}
          </span>
        )}
      </button>

      {isExpanded && (
        <div 
          style={{
            marginTop: "8px",
            padding: "8px",
            background: "rgba(0, 0, 0, 0.05)",
            borderRadius: "4px",
            fontFamily: "monospace",
            fontSize: "11px",
            maxHeight: "300px",
            overflow: "auto",
          }}
        >
          {/* Tools Used Section */}
          {toolsUsed.length > 0 && (
            <div style={{ marginBottom: "8px" }}>
              <div style={{ fontWeight: "bold", marginBottom: "4px", color: "#666" }}>
                Tools Used:
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                {toolsUsed.map((tool, idx) => (
                  <span 
                    key={idx}
                    style={{
                      background: "rgba(100, 100, 255, 0.15)",
                      padding: "2px 6px",
                      borderRadius: "3px",
                      color: "#5555aa",
                    }}
                  >
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Usage Info Section */}
          {usageInfo && (
            <div style={{ marginBottom: "8px" }}>
              <div style={{ fontWeight: "bold", marginBottom: "4px", color: "#666" }}>
                Usage:
              </div>
              <div style={{ color: "#555" }}>
                Model: {usageInfo.model} | Tier: {usageInfo.tier}
                {usageInfo.tokens && (
                  <> | Tokens: {usageInfo.tokens.input} in, {usageInfo.tokens.output} out</>
                )}
              </div>
            </div>
          )}

          {/* Debug Parts Section */}
          {debugParts.length > 0 && (
            <div>
              <div style={{ fontWeight: "bold", marginBottom: "4px", color: "#666" }}>
                Raw Parts ({debugParts.length}):
              </div>
              <pre 
                style={{ 
                  margin: 0, 
                  whiteSpace: "pre-wrap", 
                  wordBreak: "break-all",
                  color: "#555",
                  fontSize: "10px",
                }}
              >
                {JSON.stringify(debugParts, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DebugDetailsPanel;
